import type { SupportedLanguage } from "./i18n";

/**
 * Default country for the phone inputs.
 *
 * The contact step is required to continue, and it used to hardcode "US" —
 * so a German user landing on it saw a US flag and a +1 prefix and had to
 * hunt for their own country in the dropdown before they could move on.
 *
 * Resolution order:
 *   1. Region subtag of the browser's locale ("de-DE" -> DE, "fr-CA" -> CA).
 *      This is the most specific signal we have and covers the common case.
 *   2. The active UI language mapped to its primary market, for locales that
 *      carry no region ("de" -> DE).
 *   3. "US" — the largest market, and the previous behaviour.
 */

/** Primary market per supported UI language, for region-less locales. */
const LANGUAGE_FALLBACK: Record<SupportedLanguage, string> = {
  en: "US",
  de: "DE",
  es: "ES",
  fr: "FR",
};

/** Extract an ISO 3166-1 alpha-2 region from a BCP 47 tag, if it has one. */
function regionOf(locale: string | undefined): string | undefined {
  if (!locale) return undefined;
  try {
    const region = new Intl.Locale(locale).region;
    if (region && /^[A-Z]{2}$/.test(region)) return region;
  } catch {
    // Malformed tag — fall through to the manual split below.
  }
  const parts = locale.split(/[-_]/);
  const candidate = parts[1]?.toUpperCase();
  return candidate && /^[A-Z]{2}$/.test(candidate) ? candidate : undefined;
}

/**
 * @param language the active i18n language (e.g. "de"), used as the fallback
 *   when the browser locale carries no region.
 */
export function getDefaultPhoneCountry(language?: string): string {
  if (typeof navigator !== "undefined") {
    const locales = [
      ...(navigator.languages ?? []),
      navigator.language,
    ].filter(Boolean) as string[];
    for (const locale of locales) {
      const region = regionOf(locale);
      if (region) return region;
    }
  }

  const base = language?.split("-")[0] as SupportedLanguage | undefined;
  if (base && base in LANGUAGE_FALLBACK) return LANGUAGE_FALLBACK[base];

  return "US";
}

export default getDefaultPhoneCountry;
